import { GLOBAL_PREFIX } from '@/router/constants';

const PREFIX = `${GLOBAL_PREFIX}/admin`;
const buildAdminMeta = (navRouteActive) => ({
  app: 'admin',
  overlay: 'dark',
  navRouteActive,
  // The detail views read their id once, when they open; built anew for
  // another id (see getRouterViewKey).
  remountOnParamsChange: true,
});

const routes = [
  {
    path: PREFIX,
    name: 'admin-home',
    component: () => import('@/views/admin/HomeView.vue'),
    meta: {
      title: 'word.admin',
      ...buildAdminMeta('admin-home'),
    },
  },
  {
    path: `${PREFIX}/review-sessions`,
    name: 'admin-review-session-list',
    component: () => import('@/views/admin/ExternalPartySessionListView.vue'),
    meta: {
      title: 'word.sessions',
      ...buildAdminMeta('admin-review-session-list'),
    },
  },
  {
    path: `${PREFIX}/review-sessions/:uuid`,
    name: 'admin-review-session-detail',
    component: () => import('@/views/admin/ReviewSessionDetailView.vue'),
    meta: {
      title: 'word.review',
      ...buildAdminMeta('admin-review-session-list'),
    },
  },
  {
    path: `${PREFIX}/ai`,
    alias: `${PREFIX}/chat-threads`,
    name: 'admin-chat-thread-list',
    component: () => import('@/views/admin/ChatThreadListView.vue'),
    meta: {
      title: 'word.ai',
      ...buildAdminMeta('admin-chat-thread-list'),
    },
  },
  {
    path: `${PREFIX}/ai/graphs`,
    alias: `${PREFIX}/chat-threads/graphs`,
    name: 'admin-graph-list',
    component: () => import('@/views/admin/GraphListView.vue'),
    meta: {
      ...buildAdminMeta('admin-chat-thread-list'),
    },
  },
  {
    path: `${PREFIX}/ai/logs`,
    name: 'admin-ai-log-list',
    component: () => import('@/views/admin/AILogListView.vue'),
    meta: {
      ...buildAdminMeta('admin-chat-thread-list'),
    },
  },
  {
    path: `${PREFIX}/ai/prompts`,
    name: 'admin-ai-prompt-list',
    component: () => import('@/views/admin/AIPromptListView.vue'),
    meta: {
      title: 'phrase.prompts',
      ...buildAdminMeta('admin-chat-thread-list'),
    },
  },
  {
    path: `${PREFIX}/ai/prompts/:id`,
    name: 'admin-ai-prompt-detail',
    component: () => import('@/views/admin/AIPromptDetailView.vue'),
    meta: {
      title: 'word.prompt',
      ...buildAdminMeta('admin-chat-thread-list'),
    },
  },
  {
    // Declared before /ai/:id, which would otherwise match "costs" as an id.
    path: `${PREFIX}/ai/costs`,
    name: 'admin-ai-cost-overview',
    component: () => import('@/views/admin/AICostOverviewView.vue'),
    meta: {
      title: 'word.costs',
      ...buildAdminMeta('admin-chat-thread-list'),
    },
  },
  {
    path: `${PREFIX}/ai/logs/:graphRunUuid`,
    name: 'admin-ai-log-run-detail',
    component: () => import('@/views/admin/AILogRunDetailView.vue'),
    meta: {
      title: 'phrase.ai_log_run',
      ...buildAdminMeta('admin-chat-thread-list'),
    },
  },
  {
    path: `${PREFIX}/ai/:id`,
    alias: `${PREFIX}/chat-threads/:id`,
    name: 'admin-chat-thread-detail',
    component: () => import('@/views/admin/ChatThreadDetailView.vue'),
    meta: {
      title: 'word.ai_assistant',
      ...buildAdminMeta('admin-chat-thread-list'),
    },
  },
  {
    path: `${PREFIX}/researches`,
    name: 'admin-research-list',
    component: () => import('@/views/admin/ResearchListView.vue'),
    meta: {
      title: 'word.researches',
      ...buildAdminMeta('admin-research-list'),
    },
  },
  {
    path: `${PREFIX}/research-proposals`,
    name: 'admin-research-proposal-list',
    component: () => import('@/views/admin/ResearchProposalListView.vue'),
    meta: {
      title: 'word.research_proposals',
      ...buildAdminMeta('admin-research-proposal-list'),
    },
  },
  {
    path: `${PREFIX}/research-proposals/:id`,
    name: 'admin-research-proposal-detail',
    component: () => import('@/views/admin/ResearchProposalDetailView.vue'),
    meta: {
      title: 'word.research_proposal',
      ...buildAdminMeta('admin-research-proposal-list'),
    },
  },
  {
    path: `${PREFIX}/researches/:uuid`,
    name: 'admin-research-detail',
    component: () => import('@/views/admin/ResearchDetailView.vue'),
    meta: {
      title: 'word.research',
      ...buildAdminMeta('admin-research-list'),
    },
  },
  {
    path: `${PREFIX}/research-sets`,
    name: 'admin-research-set-list',
    component: () => import('@/views/admin/ResearchSetListView.vue'),
    meta: {
      title: 'word.research_sets',
      ...buildAdminMeta('admin-research-set-list'),
    },
  },
  {
    path: `${PREFIX}/research-sets/:uuid`,
    name: 'admin-research-set-detail',
    component: () => import('@/views/admin/ResearchSetDetailView.vue'),
    meta: {
      ...buildAdminMeta('admin-research-set-list'),
    },
  },
  {
    path: `${PREFIX}/researches/:researchUuid/builder`,
    name: 'admin-research-builder-v2',
    component: () => import('@/views/organisation/v2/ResearchBuilderV2.vue'),
    meta: {
      title: 'word.research_setup',
      ...buildAdminMeta('admin-research-list'),
    },
  },
  {
    path: `${PREFIX}/organisation-documents/`,
    name: 'admin-organisation-document-list',
    component: () => import('@/views/admin/OrganisationDocumentListView.vue'),
    meta: {
      title: 'word.documents',
      ...buildAdminMeta('admin-organisation-document-list'),
    },
  },
  {
    path: `${PREFIX}/items`,
    name: 'admin-item-list',
    component: () => import('@/views/admin/ItemListView.vue'),
    meta: {
      title: 'word.items',
      ...buildAdminMeta('admin-item-list'),
    },
  },
  {
    path: `${PREFIX}/items/:id`,
    name: 'admin-item-detail',
    component: () => import('@/views/admin/ItemDetailView.vue'),
    meta: {
      title: 'word.item',
      ...buildAdminMeta('admin-item-list'),
    },
  },
  {
    path: `${PREFIX}/countries`,
    name: 'admin-country-list',
    component: () => import('@/views/admin/CountryListView.vue'),
    meta: {
      title: 'word.countries',
      ...buildAdminMeta('admin-country-list'),
    },
  },
  {
    path: `${PREFIX}/countries/:countryCode`,
    name: 'admin-country-detail',
    component: () => import('@/views/admin/CountryDetailView.vue'),
    meta: {
      title: 'word.country',
      ...buildAdminMeta('admin-country-list'),
    },
  },
  {
    path: `${PREFIX}/newsletters`,
    name: 'admin-newsletter-registration-list',
    component: () => import('@/views/admin/NewsletterListView.vue'),
    meta: {
      title: 'word.newsletter',
      ...buildAdminMeta('admin-newsletter-registration-list'),
    },
  },
  {
    path: `${PREFIX}/logins`,
    name: 'admin-user-event-list',
    component: () => import('@/views/admin/LoginListView.vue'),
    meta: {
      title: 'word.logins',
      ...buildAdminMeta('admin-user-event-list'),
    },
  },
  {
    path: `${PREFIX}/styleguide`,
    name: 'admin-icon-list',
    component: () => import('@/views/admin/IconListView.vue'),
    meta: {
      title: 'word.styleguide',
      ...buildAdminMeta('admin-icon-list'),
    },
  },
  {
    path: `${PREFIX}/api-docs/:namespace?`,
    name: 'admin-api-docs',
    component: () => import('@/views/admin/ApiDocsView.vue'),
    meta: {
      title: 'phrase.api_documentation',
      ...buildAdminMeta('admin-api-docs'),
    },
  },
  {
    path: `${PREFIX}/organisations`,
    name: 'admin-organisation-list',
    component: () => import('@/views/admin/OrganisationListView.vue'),
    meta: {
      title: 'word.organisations',
      ...buildAdminMeta('admin-organisation-list'),
    },
  },
  {
    path: `${PREFIX}/organisations/:id`,
    name: 'admin-organisation-detail',
    component: () => import('@/views/admin/OrganisationDetailView.vue'),
    meta: {
      title: 'word.organisation',
      ...buildAdminMeta('admin-organisation-list'),
    },
  },
];

export default routes;
